import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api';
import { AppUser } from '../types';
import { toast } from 'sonner';

interface AuthContextType {
  user: AppUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isTeamMember: boolean;
  selfImprovementEnabled: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (name: string, email: string, password: string, phone?: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));

          // Verify token is still valid by fetching current user
          try {
            const response = await authApi.getMe();
            if (response.data.success && response.data.data) {
              setUser(response.data.data);
              localStorage.setItem('user', JSON.stringify(response.data.data));
          } else {
              // Token invalid, clear auth
              clearAuth();
            }
          } catch (error: any) {
            // Token expired or invalid
            if (error.response?.status === 401) {
              clearAuth();
            }
          }
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        clearAuth();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const clearAuth = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      const response = await authApi.login({ email, password });
      
      if (response.data.success && response.data.data) {
        const { token: newToken, user: userData } = response.data.data;
        
        // Store token and user
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(userData));
        
        setToken(newToken);
        setUser({
          _id: userData.id,
          name: userData.name,
          email: userData.email,
          role: userData.role,
          isActive: userData.isActive,
          ...(userData.clientId && { clientId: userData.clientId }),
          ...(userData.selfImprovementEnabled !== undefined && { selfImprovementEnabled: userData.selfImprovementEnabled }),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });

        toast.success('Login successful!');
        return true;
      } else {
        toast.error(response.data.error || 'Login failed');
        return false;
      }
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Login failed. Please try again.';
      toast.error(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string, phone?: string): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      const response = await authApi.register({ name, email, password, phone });
      
      if (response.data.success && response.data.data) {
        const { token: newToken, user: userData } = response.data.data;
        
        // Store token and user
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(userData));
        
        setToken(newToken);
        setUser({
          _id: userData.id,
          name: userData.name,
          email: userData.email,
          role: userData.role,
          isActive: userData.isActive,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        
        toast.success('Registration successful!');
        return true;
      } else {
        toast.error(response.data.error || 'Registration failed');
        return false;
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Registration failed. Please try again.';
      toast.error(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearAuth();
    toast.success('Logged out successfully');
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const response = await authApi.getMe();
      if (response.data.success && response.data.data) {
        setUser(response.data.data);
        localStorage.setItem('user', JSON.stringify(response.data.data));
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      // If refresh fails, token might be invalid
      if ((error as any).response?.status === 401) {
        clearAuth();
      }
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      const response = await authApi.changePassword({ currentPassword, newPassword });
      
      if (response.data.success) {
        toast.success('Password changed successfully!');
        return true;
      } else {
        toast.error(response.data.error || 'Failed to change password');
        return false;
      }
    } catch (error: any) {
      console.error('Change password error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to change password';
      toast.error(errorMessage);
      return false;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.role === 'admin',
    isTeamMember: user?.role === 'team_member',
    selfImprovementEnabled: user?.selfImprovementEnabled ?? false,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
